import classNames from "classnames"
import type {
  ComponentPropsWithoutRef,
  Ref,
} from "react"
import {
  forwardRef,
  useMemo,
} from "react"
import { defaultValidityState } from "./defaultValidityState"
import { useValidationContext } from "./ValidationContext"
import styles from "./ValidationMessage.module.scss"
import type { ValidityStateViolation } from "./ValidityStateViolation"
import { validityStateViolations } from "./ValidityStateViolation"
import { ExclamationFillIcon } from "src/icons"

// maps a key to a style class in ActionButton.module.scss
export enum StylePreset {
  absolute = "validationMessageAbsolute",
  static = "validationMessageStatic",
}

type StylePresetKey = keyof typeof StylePreset
export const defaultStylePreset: StylePresetKey = "static"

type DivAttributes = ComponentPropsWithoutRef<"div">

interface Props extends DivAttributes {
  invalidMessage: string | Partial<Record<ValidityStateViolation, string>>;
  stylePreset?: StylePresetKey;
}

export const ValidationMessage = forwardRef<HTMLDivElement, Props>(
  function ValidationMessage(props: Props, propRef: Ref<HTMLDivElement>) {
    const {
      invalidMessage,
      stylePreset = defaultStylePreset,
      ...htmlDivProps
    } = props

    const validationContext = useValidationContext()
    const validityState = validationContext?.validityState ?? defaultValidityState
    const showValidationMessage = validationContext?.showValidationMessage ?? false

    const message = useMemo(
      () => {
        if (!showValidationMessage) {
          return null
        }

        if (validityState.valid) {
          return null
        }

        if (typeof invalidMessage === "string") {
          return invalidMessage
        }

        const violation = validityStateViolations.find(
          (potentialViolation: ValidityStateViolation) => validityState[potentialViolation],
        )

        if (violation) {
          return invalidMessage[violation] ?? null
        }

        return null
      },
      [
        showValidationMessage,
        invalidMessage,
        validityState,
      ],
    )

    if (message == null) {
      return null
    }

    const className = classNames(
      htmlDivProps.className,
      styles[StylePreset[stylePreset]],
    )

    return (
      <div
        {...htmlDivProps}
        className={className}
        ref={propRef}
      >
        <ExclamationFillIcon className={styles.validationMessageIcon}/>
        {message}
      </div>
    )
  },
)
